import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, FileSpreadsheet, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "@/lib/api";
import { fmtEur, fmtDate, STATUS_LABELS, STATUS_CLASSES } from "@/lib/format";
import { TaxBreakdown } from "@/components/TaxBreakdown";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function DeclarationDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/declarations/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/declarations/${id}/status`, { status });
      setD((p) => ({ ...p, status: data.status }));
      toast.success(`Statut : ${STATUS_LABELS[status]}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const recompute = async () => {
    try {
      const { data } = await api.post(`/declarations/${id}/recalculer`);
      setD(data);
      toast.success("Déclaration recalculée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportAs = async (fmt) => {
    try {
      await downloadFile(`/declarations/${id}/export/${fmt}`, `declaration.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="text-muted-foreground">Chargement…</div>;
  const form = d.type === "annuelle" ? "2502-SD" : "2501-SD";

  return (
    <div className="space-y-6" data-testid="declaration-detail-page">
      <Link to="/declarations" className="inline-flex items-center text-sm text-muted-foreground hover:text-brand-600 dark:text-brand-300" data-testid="declaration-back-link"><ArrowLeft className="h-4 w-4 mr-1" /> Retour aux déclarations</Link>
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="eyebrow">Formulaire n° {form} · {d.company.name}</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1" data-testid="declaration-title">{d.label}</h1>
          <div className="text-sm text-muted-foreground mt-1">Date limite de dépôt : <span className="font-mono">{fmtDate(d.echeance)}</span>{d.company.siret && <> · SIRET <span className="font-mono">{d.company.siret}</span></>}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Select value={d.status} onValueChange={setStatus}>
            <SelectTrigger className={`w-40 border ${STATUS_CLASSES[d.status]}`} data-testid="declaration-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`declaration-status-${k}`}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={recompute} data-testid="declaration-recompute-button"><RefreshCw className="h-4 w-4 mr-2" /> Recalculer</Button>
          <Button variant="outline" onClick={() => exportAs("csv")} data-testid="declaration-export-csv-button"><FileText className="h-4 w-4 mr-2" /> CSV</Button>
          <Button variant="outline" onClick={() => exportAs("xlsx")} data-testid="declaration-export-xlsx-button"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</Button>
          <Button onClick={() => exportAs("pdf")} data-testid="declaration-export-pdf-button"><FileDown className="h-4 w-4 mr-2" /> PDF {form.slice(0, 4)}</Button>
        </div>
      </div>

      <TaxBreakdown result={d.result} />

      {d.type === "annuelle" && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="kpi-card" style={{ "--kpi-accent": "#7161D0" }}><div className="eyebrow">Taxe nette annuelle</div><div className="font-mono text-2xl font-semibold mt-2">{fmtEur(d.result.taxe_nette)}</div></div>
          <div className="kpi-card" style={{ "--kpi-accent": "#D97706" }}><div className="eyebrow">Acomptes versés (2501)</div><div className="font-mono text-2xl font-semibold mt-2" data-testid="declaration-acomptes">{fmtEur(d.acomptes_verses)}</div></div>
          <div className="kpi-card" style={{ "--kpi-accent": d.solde >= 0 ? "#DC2626" : "#059669" }}><div className="eyebrow">{d.solde >= 0 ? "Solde à payer" : "Excédent à imputer"}</div><div className="font-mono text-2xl font-semibold mt-2" data-testid="declaration-solde">{fmtEur(Math.abs(d.solde))}</div></div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-border eyebrow">Annexe — détail par salarié ({d.result.effectif})</div>
        <Table data-testid="declaration-employees-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Salarié</TableHead><TableHead>Poste</TableHead>
              <TableHead className="text-right">Rémunération</TableHead>
              <TableHead className="text-right">Base A</TableHead><TableHead className="text-right">Base B</TableHead><TableHead className="text-right">Base C</TableHead>
              <TableHead className="text-right">Taxe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.result.salaries.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.nom} {s.prenom}</TableCell>
                <TableCell className="text-muted-foreground">{s.poste || "—"}</TableCell>
                <TableCell className="text-right font-mono">{fmtEur(s.remuneration)}</TableCell>
                <TableCell className="text-right font-mono text-brand-600 dark:text-brand-300">{fmtEur(s.base1)}</TableCell>
                <TableCell className="text-right font-mono text-amber-700 dark:text-amber-400">{fmtEur(s.base2)}</TableCell>
                <TableCell className="text-right font-mono text-red-700 dark:text-red-400">{fmtEur(s.base3)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{fmtEur(s.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
